import React from "react"
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
} from "@/components/ui/card"
import { Separator } from "@/components/ui/separator"
import { Loader2 } from "lucide-react"

export interface BenefitsInfo {
  flexibleTimeOff: boolean
  FourOOneK: boolean
  medical: boolean
  retention: boolean
  vacationCredit: boolean
  sickAmount: number
  sickRollover: boolean
  conferenceBonus: boolean
  UHCEligibilityStatus: string
}

interface BenefitsCardProps {
  title: string
  description?: string
  data?: {
    status?: string
    role?: string
    benefitsInfo?: BenefitsInfo
  }
  loading: boolean
}

const BenefitsCard: React.FC<BenefitsCardProps> = ({
  title,
  description,
  data,
  loading,
}) => {
  if (loading) {
    return (
      <Card className="p-4">
        <CardHeader className="p-0 pb-3">
          <CardTitle className="text-base font-semibold">
            {title}
          </CardTitle>
        </CardHeader>

        <Separator />

        <div className="flex items-center gap-2 pt-4 text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          <span>Loading Benefits Info...</span>
        </div>
      </Card>
    )
  }

  if (!data?.benefitsInfo) {
    return (
      <Card className="p-4">
        <CardHeader className="p-0 pb-3">
          <CardTitle className="text-base font-semibold">
            {title}
          </CardTitle>
        </CardHeader>

        <Separator />

        <p className="pt-4 text-muted-foreground">
          No Benefits Found
        </p>
      </Card>
    )
  }

  const { status, role, benefitsInfo } = data

  const benefitRows: string[] = []

  // Incentive Compensation rule
  if (
    !(
      (role === "Analyst" || role === "Student Analyst") &&
      status === "Non-Exempt"
    )
  ) {
    benefitRows.push("Incentive Compensation")
  }

  if (benefitsInfo.retention)
    benefitRows.push("Retention Incentive Compensation")

  if (benefitsInfo.flexibleTimeOff)
    benefitRows.push("Flexible Time Off")

  if (benefitsInfo.sickAmount > 1)
    benefitRows.push(
      `Paid Sick Leave up to ${benefitsInfo.sickAmount} hours`
    )

  if (benefitsInfo.sickAmount === 1)
    benefitRows.push("Paid Sick Leave")

  if (benefitsInfo.sickRollover)
    benefitRows.push("Sick Leave Rollover")

  if (benefitsInfo.conferenceBonus)
    benefitRows.push("Conference Bonus")

  if (benefitsInfo.FourOOneK)
    benefitRows.push("401(k)")

  if (benefitsInfo.medical)
    benefitRows.push("Medical, Vision & Dental")

  if (role === "Analyst")
    benefitRows.push("Online CEUs")

  return (
    <Card className="p-4 space-y-4">
      <CardHeader className="p-0">
        <CardTitle className="text-base font-semibold">
          {title}
        </CardTitle>
      </CardHeader>

      <Separator />

      <CardContent className="p-0 pt-2 space-y-1">
        {benefitRows.map((benefit, i) => (
          <p key={i} className="text-sm">
            • {benefit}
          </p>
        ))}
      </CardContent>

      <Separator />

      <p className="text-xs text-muted-foreground">
        For more information, please refer to the{" "}
        <a
          href="https://sharepoint.teampbs.com/sites/My/Pages/HR-Landing.aspx"
          target="_blank"
          rel="noopener noreferrer"
          className="underline hover:text-foreground"
        >
          PBS Handbook
        </a>.
      </p>

      <Separator />

      <p className="text-sm">
        <span className="font-medium">
          Current Health Insurance Eligibility Status:
        </span>{" "}
        {benefitsInfo.UHCEligibilityStatus}
      </p>
    </Card>
  )
}

export default BenefitsCard